import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { IndianRupee, PackagePlus, Percent, Wallet } from "lucide-react";

const money = (v) => `₹${Number(v || 0).toFixed(2)}`;

const STATUS_TONE = {
  pending: "border-amber-500/50 text-amber-700 bg-amber-500/10",
  live: "border-primary/40 text-primary bg-primary/10",
  rejected: "border-destructive/40 text-destructive bg-destructive/10",
};

/** The same four figures for both systems, so they read as directly comparable. */
const Figures = ({ totals = {}, code, prefix }) => (
  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
    {[["This month", "this_month"], ["Lifetime", "lifetime"],
      ["Paid", "paid"], ["Pending", "pending"]].map(([label, key]) => (
      <div key={key} className="flex justify-between">
        <span className="text-muted-foreground">{label}</span>
        <span className={key === "pending" ? "font-medium" : ""}
              data-testid={`${prefix}-${key}-${code}`}>
          {money(totals[key])}
        </span>
      </div>
    ))}
  </div>
);

/**
 * Admin → Doctor products & payouts. Two jobs, deliberately side by side:
 * publish what a doctor partner proposed (setting the share for that product), and
 * read what is owed. Referral and product share are shown as separate figures and
 * are never added together — they are different money with different ledgers.
 */
export const DoctorProductsTab = () => {
  const [queue, setQueue] = useState({ submissions: [], counts: {}, statuses: [] });
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);
  const [paying, setPaying] = useState(null);
  const [payment, setPayment] = useState({ amount: "", note: "", paid_on: "" });
  const [rateDraft, setRateDraft] = useState({});

  const load = () => {
    api.get("/admin/product-submissions", { params: { status } })
      .then(({ data }) => setQueue(data)).catch(() => {});
    api.get("/admin/payouts").then(({ data }) => setRows(data.payouts || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [status]);

  const approve = async () => {
    try {
      await api.post(`/admin/product-submissions/${review.id}/approve`, {
        name: review.name,
        description: review.description,
        price: parseFloat(review.price) || 0,
        stock: parseInt(review.stock, 10) || 0,
        category: review.category || "general",
        share_pct: parseFloat(review.share_pct) || 0,
      });
      toast.success(`${review.name} is live in the shop`);
      setReview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reject = async () => {
    const reason = window.prompt("Why is this product not being published?") || "";
    try {
      await api.post(`/admin/product-submissions/${review.id}/reject`, { reason });
      toast.success("The doctor will see it was not accepted");
      setReview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const recordPayment = async () => {
    // Two logs, deliberately: commission payments sit with the referral programme,
    // product-share payments have their own ledger.
    const url = paying.kind === "commission"
      ? `/admin/doctor-partners/${paying.partner_id}/payments`
      : `/admin/doctor-partners/${paying.partner_id}/product-share-payouts`;
    try {
      await api.post(url, {
        amount: parseFloat(payment.amount) || 0,
        note: payment.note,
        paid_on: payment.paid_on,
      });
      toast.success(`${money(payment.amount)} recorded against ${paying.name}'s ${
        paying.kind === "commission" ? "commission" : "product share"}`);
      setPaying(null);
      setPayment({ amount: "", note: "", paid_on: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveRate = async (row) => {
    const rate = parseFloat(rateDraft[row.partner_id] ?? row.commission_pct) || 0;
    try {
      await api.patch(`/admin/doctor-partners/${row.partner_id}/commission`, { commission_pct: rate });
      toast.success(rate > 0
        ? `${row.name} now earns ${rate}% on referred orders paid from now on`
        : `${row.name} no longer earns commission`);
      setRateDraft({ ...rateDraft, [row.partner_id]: undefined });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const backfill = async (row) => {
    if (!window.confirm(
      `Write commission for ${row.name}'s already-paid orders at ${row.commission_pct}%? ` +
      "Orders that already have an entry are left as they are."
    )) return;
    try {
      const { data } = await api.post(`/admin/doctor-partners/${row.partner_id}/commission/backfill`);
      toast.success(data.entries_written
        ? `${data.entries_written} order(s) added · ${money(data.amount)} accrued`
        : "Nothing new to add — every paid order already has an entry");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="doctor-products-tab">
      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h3 className="font-display text-2xl flex items-center gap-2 mb-1">
            <PackagePlus size={18} /> Products proposed by doctors
          </h3>
          <p className="text-sm text-muted-foreground mb-5">
            The doctor's price and stock are a suggestion. Confirm them, set the share for this
            product, and it goes live in the shop under their name.
          </p>

          <div className="flex flex-wrap gap-2 mb-5">
            {["all", ...(queue.statuses || [])].map((value) => (
              <Button
                key={value}
                size="sm"
                variant={status === value ? "default" : "outline"}
                className="btn-pill h-8 text-xs capitalize"
                onClick={() => setStatus(value)}
                data-testid={`sub-filter-${value}`}
              >
                {value}{value !== "all" && queue.counts?.[value] ? ` (${queue.counts[value]})` : ""}
              </Button>
            ))}
          </div>

          {(queue.submissions || []).length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="sub-empty">
              No product submissions to show.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sub-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-normal">Product</th>
                    <th className="py-2 pr-4 font-normal">Doctor</th>
                    <th className="py-2 pr-4 font-normal">Price</th>
                    <th className="py-2 pr-4 font-normal">Stock</th>
                    <th className="py-2 pr-4 font-normal">Share</th>
                    <th className="py-2 pr-4 font-normal">Sold</th>
                    <th className="py-2 pr-4 font-normal">Status</th>
                    <th className="py-2 font-normal" />
                  </tr>
                </thead>
                <tbody>
                  {queue.submissions.map((s) => (
                    <tr key={s.id} className="border-b border-border/60" data-testid={`sub-row-${s.id}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          {s.photo && (
                            <img src={mediaUrl(s.photo)} alt="" className="w-9 h-9 rounded-lg object-cover border border-border" />
                          )}
                          <span>{s.name}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div>{s.partner_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{s.partner_code}</div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">{money(s.status === "live" ? s.live_price : s.price)}</td>
                      <td className="py-3 pr-4">{s.status === "live" ? s.live_stock : s.stock}</td>
                      <td className="py-3 pr-4">{s.share_pct ? `${s.share_pct}%` : "—"}</td>
                      <td className="py-3 pr-4">{s.units_sold || 0}</td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className={`${STATUS_TONE[s.status]} text-[10px] capitalize`}>
                          {s.status === "live" ? "Live" : s.status}
                        </Badge>
                      </td>
                      <td className="py-3 text-right">
                        {s.status === "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="btn-pill h-8"
                            onClick={() => setReview({ ...s, category: "general", share_pct: "" })}
                            data-testid={`sub-review-${s.id}`}
                          >
                            Review
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-6 lg:p-8">
          <h3 className="font-display text-2xl flex items-center gap-2 mb-1">
            <Wallet size={18} /> Payouts
          </h3>
          <p className="text-sm text-muted-foreground mb-5">
            Referral commission and product share are separate systems with separate ledgers and
            separate payment logs — the figures are never added together. A line item that earned
            product share is left out of the commission base, so nothing is counted twice.
          </p>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="payouts-empty">
              No approved doctor partners yet.
            </p>
          ) : (
            <div className="space-y-4" data-testid="payouts-list">
              {rows.map((r) => (
                <div key={r.partner_id} className="rounded-xl border border-border p-4 lg:p-5"
                     data-testid={`payout-row-${r.code}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                    <div>
                      <span className="font-display text-xl">{r.name}</span>
                      <span className="text-xs text-muted-foreground font-mono ml-2">{r.code}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {r.referral_orders} paid order(s) attributed · {money(r.referral_revenue)} of sales
                    </span>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-muted/40 p-4">
                      <p className="overline flex items-center gap-1.5 mb-3">
                        <Percent size={12} /> Referral commission
                      </p>
                      <div className="flex items-center gap-2 mb-3">
                        <Input
                          type="number"
                          step="0.01"
                          className="h-9 w-24 bg-transparent"
                          placeholder="rate %"
                          value={rateDraft[r.partner_id] ?? (r.commission_pct || "")}
                          onChange={(e) => setRateDraft({ ...rateDraft, [r.partner_id]: e.target.value })}
                          data-testid={`commission-rate-${r.code}`}
                        />
                        <Button size="sm" variant="outline" className="btn-pill h-9"
                                onClick={() => saveRate(r)} data-testid={`commission-rate-save-${r.code}`}>
                          Save rate
                        </Button>
                        <Button size="sm" variant="ghost" className="btn-pill h-9"
                                onClick={() => backfill(r)} data-testid={`commission-backfill-${r.code}`}>
                          Backfill history
                        </Button>
                      </div>
                      {!r.commission_pct && (
                        <p className="text-xs text-muted-foreground mb-3">
                          No rate set, so this doctor earns no commission yet.
                        </p>
                      )}
                      <Figures totals={r.commission} code={r.code} prefix="commission" />
                      <Button size="sm" variant="outline" className="btn-pill h-8 mt-3"
                              onClick={() => setPaying({ ...r, kind: "commission" })}
                              data-testid={`commission-pay-${r.code}`}>
                        <IndianRupee size={12} className="mr-1" /> Record commission payment
                      </Button>
                    </div>

                    <div className="rounded-lg bg-muted/40 p-4">
                      <p className="overline flex items-center gap-1.5 mb-3">
                        <PackagePlus size={12} /> Product share
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        {r.product_share?.units || 0} unit(s) of their own products sold. The rate is set
                        per product in the Products tab.
                      </p>
                      <Figures totals={r.product_share} code={r.code} prefix="share" />
                      <Button size="sm" variant="outline" className="btn-pill h-8 mt-3"
                              onClick={() => setPaying({ ...r, kind: "share" })}
                              data-testid={`payout-pay-${r.code}`}>
                        <IndianRupee size={12} className="mr-1" /> Record share payment
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(review)} onOpenChange={(o) => { if (!o) setReview(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Publish this product</DialogTitle>
          </DialogHeader>
          {review && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Proposed by {review.partner_name} ({review.partner_code}).
              </p>
              {review.photo && (
                <img src={mediaUrl(review.photo)} alt="" className="w-24 h-24 rounded-xl object-cover border border-border" />
              )}
              <div><Label>Name</Label>
                <Input value={review.name} onChange={(e) => setReview({ ...review, name: e.target.value })}
                       data-testid="sub-name" /></div>
              <div><Label>Description</Label>
                <Textarea rows={3} value={review.description || ""}
                          onChange={(e) => setReview({ ...review, description: e.target.value })}
                          data-testid="sub-desc" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Price (₹)</Label>
                  <Input type="number" value={review.price}
                         onChange={(e) => setReview({ ...review, price: e.target.value })}
                         data-testid="sub-price" /></div>
                <div><Label>Stock</Label>
                  <Input type="number" value={review.stock}
                         onChange={(e) => setReview({ ...review, stock: e.target.value })}
                         data-testid="sub-stock" /></div>
                <div><Label>Share %</Label>
                  <Input type="number" step="0.01" value={review.share_pct}
                         onChange={(e) => setReview({ ...review, share_pct: e.target.value })}
                         placeholder="e.g. 12.5" data-testid="sub-share" /></div>
              </div>
              <div><Label>Category</Label>
                <Input value={review.category || "general"}
                       onChange={(e) => setReview({ ...review, category: e.target.value })}
                       data-testid="sub-category" /></div>
              <div className="flex gap-2 pt-2">
                <Button className="btn-action h-10 flex-1" onClick={approve} data-testid="sub-approve">
                  Approve &amp; publish
                </Button>
                <Button variant="outline" className="btn-pill h-10 text-destructive" onClick={reject}
                        data-testid="sub-reject">
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(paying)} onOpenChange={(o) => { if (!o) setPaying(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {paying?.kind === "commission" ? "Record a commission payment" : "Record a product-share payment"}
            </DialogTitle>
          </DialogHeader>
          {paying && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {paying.name} · pending {money(paying.kind === "commission"
                  ? paying.commission?.pending : paying.product_share?.pending)}. This log is kept
                separate from {paying.kind === "commission" ? "product-share" : "commission"} payments.
              </p>
              <div><Label>Amount (₹)</Label>
                <Input type="number" value={payment.amount}
                       onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                       data-testid="share-pay-amount" /></div>
              <div><Label>Paid on</Label>
                <Input type="date" value={payment.paid_on}
                       onChange={(e) => setPayment({ ...payment, paid_on: e.target.value })}
                       data-testid="share-pay-date" /></div>
              <div><Label>Note</Label>
                <Input value={payment.note} placeholder="UPI reference, cheque no."
                       onChange={(e) => setPayment({ ...payment, note: e.target.value })}
                       data-testid="share-pay-note" /></div>
              <Button className="btn-action h-10 w-full" onClick={recordPayment} data-testid="share-pay-save">
                Save payment
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorProductsTab;
